import { useEffect, useState } from 'react';
import * as kpiApi from './api/kpiReport';
import {
  getCurrentYm, getBaseMonths, getLastYearMonths, getMonthEndDate, formatDateYmd,
  calcThroughputTrend, calcFinalDefectTrend, calcWipTrend, calcDeliveryShortenRate,
  calcProducedQtyCard, calcOtdCard,
} from './pqcdTrend';

/**
 * 메인 대시보드 "핵심성과지표" 패널 + KPI 리포트 "PQCD 종합 스코어" 레이더가 공통으로 쓰는
 * 데이터 로딩 + Phase 4(2026-10, 항목별 자동 베이스라인) 계산을 한 곳에 모은 훅.
 *
 * 원래 이 로직은 MainDashboardPage 안에 있었다. KPI 리포트 대시보드 탭도 같은 계산(같은
 * 자동 베이스라인 규칙)이 필요해지면서, 페이지마다 복붙하면 두 화면 숫자가 다시 갈릴 수
 * 있어 훅으로 뺐다 - "계산은 pqcdTrend.js(순수 함수), 로딩·연동은 여기, 표시는 각 페이지"로
 * 책임을 나눈다.
 *
 * 필요한 연도 = {올해, 작년} 뿐이다(Phase 4부터 셀렉터가 없어 더 간단해졌다) - recent3(지난달
 * 로 끝나는 3개월)가 걸칠 수 있는 연도는 항상 올해 아니면 작년뿐이다(3개월 전이라 재작년으로는
 * 못 넘어간다). 재공재고(C)만 "시점 스냅샷"이라 연도별 전체 조회로 안 되고, chooseBaseline이
 * 사후에 lastYear/recent3 중 어느 쪽을 고를지 몰라 두 모드가 필요로 하는 월말 스냅샷을
 * (작년 12개월 + 최근 3개월, 겹치면 한 번만) 전부 요청해 둔다.
 */
const usePqcdData = () => {
  const [pqcdProduction, setPqcdProduction] = useState([]);
  const [pqcdQuality, setPqcdQuality] = useState([]);
  const [pqcdContractLead, setPqcdContractLead] = useState([]);
  const [pqcdDelivery, setPqcdDelivery] = useState([]); // 카드 "제조납기준수율" 전용
  const [pqcdWipSnapshots, setPqcdWipSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const today = new Date();
    const thisYear = today.getFullYear();
    const lastYear = thisYear - 1;
    const years = [thisYear, lastYear];

    // 연도별로 4개 API(생산·품질·계약리드타임·납기)를 부른다. results 는 연도 순서대로 4개씩
    // 묶여 오므로, 지표별로 다시 뽑아 두 해 목록을 이어붙인다. 재공재고는 아래 별도 요청
    // (wipRequests)으로 뺐다 - 월별 실적 목록이 아니라 시점 스냅샷이라 성격이 다르다.
    const yearRequests = years.flatMap((y) => {
      const p = { searchYear: y };
      return [
        kpiApi.getProductionKpi(p),
        kpiApi.getQualityKpi(p),
        kpiApi.getContractLeadKpi(p),
        kpiApi.getDeliveryKpi(p),
      ];
    });
    const REQS_PER_YEAR = 4;
    const pickList = (results, offset) => years
      .flatMap((_, i) => results[i * REQS_PER_YEAR + offset]?.value?.data?.list || []);

    // 재공재고(C) 스냅샷 - lastYear 모드면 작년 12개월 말일, recent3 모드면 최근 3개월
    // 말일이 필요하다. 어느 쪽이 될지(그 항목에 작년 데이터가 있는지)는 스냅샷을 받아보기
    // 전엔 알 수 없으니, 둘 다(겹치는 달은 Set으로 한 번만) + 오늘(이번달)을 요청해 둔다.
    const currentYm = getCurrentYm(today);
    const lastYearMonths = getLastYearMonths(today);
    const recent3Months = getBaseMonths(today);
    const wipYms = Array.from(new Set([...lastYearMonths, ...recent3Months]));
    const wipTargets = [
      ...wipYms.map((ym) => ({ kpiYm: ym, asOfDt: getMonthEndDate(ym) })),
      { kpiYm: currentYm, asOfDt: formatDateYmd(today) },
    ];
    const wipRequests = wipTargets.map((t) => kpiApi.getWipSnapshot({ asOfDt: t.asOfDt }));

    Promise.allSettled([...yearRequests, ...wipRequests]).then((results) => {
      setPqcdProduction(pickList(results, 0));
      setPqcdQuality(pickList(results, 1));
      setPqcdContractLead(pickList(results, 2));
      setPqcdDelivery(pickList(results, 3));

      const wipResults = results.slice(yearRequests.length);
      setPqcdWipSnapshots(wipTargets.map((t, i) => {
        const settled = wipResults[i];
        // 요청 자체가 실패(rejected)하면 null 로 남겨 calcWipTrend 가 그 달을 평균에서 빼게 한다.
        if (settled?.status !== 'fulfilled') {
          return { kpiYm: t.kpiYm, wipQty: null, wipPlanCnt: null };
        }
        const row = settled.value?.data?.list?.[0];
        // existPlanCnt(그 시점에 존재한 계획 수) 가 0이면 "그때는 아직 시스템을 안 쓰고
        // 있었다" 는 뜻이라 데이터 없음(null)으로 뺀다 - 안 그러면 도입 초기 몇 달의
        // wipQty=0(계획이 없어서 0) 이 "실제로 재공을 다 소진한 0" 과 섞여 평균을 끌어내린다.
        // existPlanCnt > 0 인데 wipQty 가 0이면 그건 "계획은 있었는데 다 나갔다"는 진짜 0이라
        // 그대로 살린다.
        const existCnt = row?.existPlanCnt != null ? Number(row.existPlanCnt) : 0;
        if (existCnt === 0) {
          return { kpiYm: t.kpiYm, wipQty: null, wipPlanCnt: 0 };
        }
        return {
          kpiYm: t.kpiYm,
          wipQty: row?.wipQty != null ? Number(row.wipQty) : 0,
          wipPlanCnt: row?.wipPlanCnt != null ? Number(row.wipPlanCnt) : 0,
        };
      }));
    }).finally(() => setLoading(false));
    // 셀렉터가 없어 입력이 "오늘" 하나뿐이다 - 마운트 시 한 번만 불러오면 된다(날짜가 바뀌는
    // 건 페이지를 다시 열 때마다 자연히 반영된다).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const now = new Date();
  const pqcdThroughput = calcThroughputTrend(pqcdProduction, now);
  const pqcdDefect = calcFinalDefectTrend(pqcdQuality, now);
  const pqcdWipResult = calcWipTrend(pqcdWipSnapshots, now);
  const pqcdDeliveryShorten = calcDeliveryShortenRate(pqcdContractLead, now);
  const cardProduced = calcProducedQtyCard(pqcdProduction, now);
  const cardOtd = calcOtdCard(pqcdDelivery, now);

  return {
    loading,
    now,
    pqcdThroughput,
    pqcdDefect,
    pqcdWipResult,
    pqcdDeliveryShorten,
    cardProduced,
    cardOtd,
  };
};

export default usePqcdData;
